import { useState, useMemo, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import ModalWrapper from "../UI/ModalWrapper";
import CriticalityBadge from "../CriticalityBadge";
import { Icon } from "../UI/Icon";
import Selector from "./../UI/Selector"
import { fetchWithAuth } from '../../Utils/fetchWithAuth';
import usePatch from "./../../Hooks/usePatch"
import API_ENDPOINTS from "../../Config/api";
import { toast } from 'react-toastify';
import ClipLoader from 'react-spinners/ClipLoader';

export default function SuperficieEsternaEspostaTable1({ data, setData, disableForEnterprise }) {
  // ...useState, useEffect, ecc...

  const { t } = useTranslation();
  const [riskFilter, setRiskFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("non_risolte");
  const [assetFilter, setAssetFilter] = useState("");
  const [freeSearch, setFreeSearch] = useState(""); // Ricerca libera in tutti i campi
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Loading stato locale per fetch
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0); // Forza il refetch dopo la sincronizzazione

  // Funzione per sincronizzare i dati con Cyber Angel
  const handleSync = async () => {
    setSyncing(true);
    try {
      const url = `${API_ENDPOINTS.cyberSurveillanceVulnerability.replace('?', '')}?sync=true&timeout=60`;
      toast.info('Sincronizzazione in corso...', { autoClose: 2000 });
      const res = await fetchWithAuth(url);
      if (!res || !res.ok) {
        toast.error('Errore durante la sincronizzazione');
        return;
      }
      const json = await res.json();
      if (json?.sync && json.sync.ok === false) {
        toast.warning(json.sync.detail || 'Sincronizzazione non riuscita: mostro i dati in cache');
      } else {
        toast.success('Sincronizzazione completata con successo!');
      }
      // Ricarica i dati dopo la sincronizzazione (anche se già a pagina 1)
      setPage(1);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error('Error syncing:', err);
      toast.error('Errore durante la sincronizzazione');
    } finally {
      setSyncing(false);
    }
  };

  // Carica la lista degli asset per il filtro
  useEffect(() => {
    async function loadAssets() {
      try {
        const res = await fetchWithAuth(API_ENDPOINTS.allCompanyAssets);
        if (!res) {
          // Token refresh failed, user logged out
          return;
        }
        if (res.ok) {
          const json = await res.json();
          setAssets(json.results || json); // Assumendo che sia paginato o no
        }
      } catch (err) {
        console.error('Error loading assets:', err);
      }
    }
    loadAssets();
  }, []);

  // Calcola il numero totale di pagine dai dati (se disponibili)
  const totalItems = data?.count ?? (data?.results?.length ?? 0);
  const totalPages = data?.pages ?? Math.max(1, Math.ceil(totalItems / pageSize));

  // Handler paginazione
  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const resetFilters = () => {
    setRiskFilter('');
    setStatusFilter('non_risolte');
    setAssetFilter('');
    setFreeSearch('');
    setPage(1);
  };

  const riskOptions = [
    { value: '', label: t('table.all') },
    { value: 'basso', label: 'Basso' },
    { value: 'medio', label: 'Medio' },
    { value: 'alto', label: 'Alto' },
  ];
  const statusOptions = [
    { value: '', label: t('table.all') },
    { value: 'non_risolte', label: 'Non risolte' },
    { value: "open", label: t('status.open') },
    { value: "managed", label: t('status.managed') },
    { value: "resolved", label: t('status.resolved') },
  ];

  const assetOptions = [
    { value: '', label: t('table.all') },
    ...assets
      .map((asset, index) => ({
        value: asset.asset, // Usa asset name
        label: asset.asset || asset.name || asset.toString() || `Asset ${index + 1}`
      })),
  ];


  // Effettua fetch API ogni volta che cambiano i parametri
  // Sostituisci l'endpoint con quello corretto se necessario
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: page,
          page_size: pageSize,
          rischio: riskFilter,
          stato: statusFilter, // Cambiato da status a stato
          asset: assetFilter,
          free_search: freeSearch, // Ricerca libera in tutti i campi
        });
        const url = `${API_ENDPOINTS.cyberSurveillanceVulnerability}?${params.toString()}`;
        const res = await fetchWithAuth(url);
        if (!res) {
          // Token refresh failed, user logged out
          return;
        }
        const json = await res.json();
        setData(json);
      } catch (err) {
        console.error('Error fetching data:', err);
        // Gestione errore: puoi mostrare un toast o altro
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [page, pageSize, riskFilter, statusFilter, assetFilter, freeSearch, refreshKey]);

  // I dati ora sono gestiti dal backend, non serve filtrare localmente
  const filteredRows = data?.results ?? [];

  return (
    <>
      <div className="bg-white rounded-lg shadow p-4 mb-4">
        <div className="flex flex-wrap gap-4 items-center justify-between">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Ricerca:</label>
              <input
                type="text"
                value={freeSearch}
                onChange={(e) => setFreeSearch(e.target.value)}
                placeholder="Cerca..."
                className="border rounded px-3 py-1.5 text-sm w-48"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Rischio:</label>
              <select 
                value={riskFilter}
                onChange={(e) => setRiskFilter(e.target.value)}
                className="border rounded px-3 py-1.5 text-sm"
              >
                {riskOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Asset:</label>
              <select 
                value={assetFilter}
                onChange={(e) => setAssetFilter(e.target.value)}
                className="border rounded px-3 py-1.5 text-sm"
              >
                {assetOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700">Stato:</label>
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded px-3 py-1.5 text-sm"
              >
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <button onClick={resetFilters} className="px-4 py-1.5 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600">
              Reset Filtri
            </button>
            <button 
              onClick={handleSync} 
              disabled={syncing}
              className="px-4 py-1.5 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
              title="Sincronizza con Cyber Angel"
            >
              <svg 
                className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`}
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {syncing ? 'Sincronizzazione...' : 'Sincronizza'}
            </button>
          </div>
          <div className="flex gap-2 items-center">
            <button onClick={handlePrevPage} disabled={page === 1} className="px-2 py-1 rounded border bg-white text-gray-700 hover:bg-gray-100">«</button>
            <span className="px-2">{page} / {totalPages}</span>
            <button onClick={handleNextPage} disabled={page === totalPages} className="px-2 py-1 rounded border bg-white text-gray-700 hover:bg-gray-100">»</button>
            <select value={pageSize} onChange={handlePageSizeChange} className="ml-2 px-2 py-1 rounded border bg-white text-gray-700">
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>
      <div className="flex flex-col h-[500px] ">
        <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto [scrollbar-gutter:stable] border-[#D1D5DB] border rounded">
          <table className="w-full table-auto text-sm text-slate-200 border-collapse min-w-[700px]">
            <thead className="text-black text-sm sticky top-0 z-10 bg-[#F3F4F7]">
              <tr>
                <th className="px-3 py-5 text-left font-medium whitespace-nowrap">{t('table.asset')}</th>
                <th className="px-3 py-5 text-center font-medium whitespace-nowrap">{t('table.vulnerability_name')}</th>
                <th className="px-3 py-5 text-center font-medium whitespace-nowrap">{t('table.risk')}</th>
                <th className="px-3 py-5 text-center font-medium whitespace-nowrap">{t('table.detected_date')}</th>
                <th className="px-3 py-5 text-center font-medium whitespace-nowrap">{t('table.status')}</th>
                <th className="px-3 py-5 text-center font-medium whitespace-nowrap">{t('table.description')}</th>
                <th className="px-3 py-5 text-center font-medium whitespace-nowrap">{t('table.note')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length > 0 ? (
                filteredRows.map((item, i) => (
                  <Row
                    key={i}
                    item={item}
                    setData={setData}
                    data={data}
                    disableForEnterprise={disableForEnterprise}
                    onStatusSaved={() => setRefreshKey((key) => key + 1)}
                  />
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="py-6">
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Row({

  item,
  setData,
  data,
  disableForEnterprise,
  onStatusSaved

}) {

  const { t } = useTranslation();
  const STATUS_OPTIONS = [
    { value: "open", label: t('status.open') },
    { value: "managed", label: t('status.managed') },
    { value: "resolved", label: t('status.resolved') },
  ];

  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [modalContent, setModalContent] = useState({});

  const [statusSelected, setStatusSelected] = useState(item.status)
  const [nota, setNota] = useState(item.status_note);
  const [prevNota, setPrevNota] = useState();

  const { patchData, loading } = usePatch()

  const handleOpenDescriptionModal = (obj) => {
    setModalContent({ vulnerabilita: obj.vulnerabilita, description: obj.descrizione });
    setShowDescriptionModal(true);
  };

  async function handleSaveStatus(valueSelector) {
    try {
      const res = await patchData(API_ENDPOINTS.changeStatusVulnerability(item.id),
        { status: valueSelector, }
      );
      setData(prev => {
        if (!prev?.results) return prev; // safety nel caso in cui prev sia undefined/null
        return {
          ...prev,
          results: prev.results.map(f =>
            f.id === item.id ? { ...f, status: valueSelector } : f
          ),
        };
      });
      setStatusSelected(valueSelector)
      onStatusSaved()
      toast.success("Stato aggiornato");
    } catch (err) {
      toast.error(`Errore aggiornamento stato: ${err?.message ?? "errore sconosciuto"}`);
    }
  }

  async function handleSaveStatusNote(prevNota) {


    try {
      const res = await patchData(API_ENDPOINTS.changeStatusVulnerability(item.id),
        { status_note: prevNota }
      );
      setData(prev => {
        if (!prev?.results) return prev; // safety nel caso in cui prev sia undefined/null
        return {
          ...prev,
          results: prev.results.map(f =>
            f.id === item.id ? { status_note: prevNota } : f
          ),
        };
      });
      setNota(prevNota)
      setShowNoteModal(false)
      toast.success("Stato aggiornato");
    } catch (err) {
      toast.error(`Errore aggiornamento stato: ${err?.message ?? "errore sconosciuto"}`);
    }
  }



  return (
    <>
      <tr className="text-black border-white border-b-4 bg-[#E3E3E3]">

        {/* Asset */}
        <td className="px-3 py-3">
          <div className="font-semibold leading-tight">
            {Array.isArray(item.asset_aliases) && item.asset_aliases.length > 0
              ? item.asset_aliases.join(", ")
              : item.asset ?? "--"}
            {item.technology_tags.map((name, index) => {


              return (
                <span key={index} className="ml-2 px-2 py-0.5 text-xs rounded bg-gray-100 border border-gray-500 w-fit mt-1 font-normal inline-block">
                  {name}
                </span>
              )
            })}
          </div>
        </td>

        {/* Nome vulnerabilità */}
        <td className="px-3 py-3 text-center tabular-nums">
          {item.vulnerabilita ?? "--"}
        </td>

        {/* Rischio */}
        <td className="px-3">
          <CriticalityBadge livello={item.rischio} />
        </td>

        {/* Data rilevamento */}
        <td className="px-3 py-3 text-center tabular-nums">
          {item.data_rilevamento ?? "--"}
        </td>



        {/* Stato */}
        <td className="px-3 py-3 text-center tabular-nums">
          {loading && !showNoteModal ?
            <ClipLoader size={15} />
            :
            !disableForEnterprise ?
              <Selector
                options={STATUS_OPTIONS}
                value={item.status}
                onChange={handleSaveStatus}
                width="w-36"
                rightIcon={false}
              /> : STATUS_OPTIONS.find(opt => opt.value === item.status)?.label || '-'
          }
        </td>

        {/* Descrizione */}
        <td className="px-3 py-3 text-center tabular-nums">
          <button className="hover:scale-105 duration-300" onClick={() => handleOpenDescriptionModal(item)}>
            <Icon name={"eye"} />
          </button>
        </td>

        {/* Nota */}
        <td className="px-3 py-3 text-center tabular-nums">
          <button className="hover:scale-105 duration-300" onClick={() => setShowNoteModal(true)}>
            <Icon name={"edit"} />
          </button>
        </td>
      </tr>

      <ModalWrapper position="center" isOpen={showDescriptionModal} onClose={() => setShowDescriptionModal(false)}>
        <div className="bg-[#F3F4F7] rounded-lg shadow-md p-6 w-[95vw] md:min-w-[400px] max-w-lg mx-auto">
          <div className="flex gap-2 items-center mb-4">
            <p className="text-lg font-semibold">{t('table.vulnerability')}: </p>
            <p>{modalContent.vulnerabilita ?? "-"}</p>
          </div>
          <p className="text-sm text-gray-700 whitespace-pre-line">{modalContent.description || "-"}</p>
        </div>
      </ModalWrapper>

      <ModalWrapper position="center" isOpen={showNoteModal} onClose={() => setShowNoteModal(false)}>

        {!disableForEnterprise ?
          <div className="bg-[#F3F4F7] rounded-lg shadow-md p-6 w-[95vw] md:w-[600px] max-w-2xl mx-auto">
            <div className="flex flex-col gap-2 mb-4 mt-4">
              <p className="text-sm text-left font-semibold text-black">{t('table.note')}</p>
              <textarea
                name=""
                id=""
                className="w-full min-h-[120px] rounded-md border border-gray-300 px-3 py-2 bg-white text-black placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-y"
                placeholder={t('table.add_note_placeholder')}
                value={nota}
                onChange={(e) => setPrevNota(e.target.value)}
              />
            </div>

            <div className="mt-2 flex items-center justify-end gap-2">
              <button
                type="button"
                className="px-3 py-2 rounded border border-gray-300 bg-white text-black hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300"
                onClick={() => setShowNoteModal(false)}
              >
                {t('table.cancel')}
              </button>
              <button
                type="button"
                className="px-3 py-2 rounded bg-blue-500 text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                onClick={() => handleSaveStatusNote(prevNota)}
              >
                {t('table.save')}
              </button>
            </div>
          </div>
          :
            <div className="bg-[#F3F4F7] rounded-lg shadow-md p-6 w-[95vw] md:min-w-[400px] max-w-lg mx-auto">
            <div className="flex gap-2 items-center mb-4">
              <p className="text-lg font-semibold">{t('table.note')}</p>
            </div>
            <p className="text-sm text-gray-700 whitespace-pre-line">{nota || t('table.no_notes')}</p>
          </div>
        }
      </ModalWrapper>


    </>
  );
}




